import { SHARE_RESULTS as LINK_SHARE_RESULTS, shareLink } from './shareLink';

/**
 * Share a picture, and fall back to the link when the picture cannot travel
 * (SPEC-005 D-6, generalised by SPEC-020 D-5).
 *
 * This was `sharePlantSheet.js`, which was written for one caller and contained
 * three things — how to build a file payload iOS will not collapse, how to try
 * it, and where to land when it fails. None of them is about plants, and all
 * three are true of the app's own invitation card, so the mechanism lives here
 * and both callers are thin.
 *
 * The order is deliberate and one-directional. A file share is the only thing
 * that works in the places people actually post — Instagram will not take a link
 * at all, Messages renders one as a grey rectangle — so it is tried first.
 * Everything that can go wrong on the way (no `navigator.share`, no file
 * support, a picture that has not been rendered, a fetch that times out, a phone
 * with no signal) lands on `shareLink`, which is what these buttons did before
 * pictures existed. Nobody loses a behaviour.
 */

/**
 * Everything `shareLink` can answer, plus the one outcome only this module has.
 *
 * Derived rather than restated: the two lists were once written out separately
 * and had already drifted by one entry, which is the failure mode a second copy
 * of a vocabulary always has. `shareLink` owns the base set because it owns the
 * fallbacks that produce it.
 */
export const SHARE_RESULTS = ['shared-image', ...LINK_SHARE_RESULTS];

/**
 * The file payload carries no `url`.
 *
 * iOS collapses a mixed `files` + `url` share back into a link share, which
 * would silently undo the whole feature on the one platform it matters most on.
 * The address rides inside `text` instead, so a recipient can still get back.
 */
function filePayload({ title, text, url }, file) {
    return { files: [file], title, text: url ? `${text} — ${url}` : text };
}

async function shareTheFile(nav, payload, file) {
    if (typeof nav.canShare !== 'function' || !nav.canShare({ files: [file] })) {
        return null;
    }

    try {
        await nav.share(filePayload(payload, file));

        return 'shared-image';
    } catch (error) {
        // Dismissing the sheet is a decision, not a failure. Falling through to
        // the link would share something the user just declined to send.
        if (error?.name === 'AbortError') {
            return 'cancelled';
        }

        return null;
    }
}

/**
 * `loadFile` is a thunk rather than a URL, because what a caller has to do to
 * produce its file differs — a plant needs its id and its name, the app needs
 * the language on screen — and none of that belongs to the ladder.
 */
export async function shareImage(
    { title, text, url },
    loadFile,
    nav = typeof navigator === 'undefined' ? null : navigator,
) {
    if (!nav) {
        return 'unavailable';
    }

    const payload = { title, text, url };

    let file = null;

    try {
        file = await loadFile();
    } catch {
        // No picture, no network, no File constructor: all the same answer here.
        return shareLink(payload, nav);
    }

    if (typeof nav.share === 'function') {
        const result = await shareTheFile(nav, payload, file);

        if (result) {
            return result;
        }
    }

    // A browser that cannot take the file gets the link, which is precisely what
    // these buttons did before the pictures existed. There is deliberately no
    // silent download here: a file appearing in someone's Downloads folder
    // because the share sheet happened to be missing is a surprise, not a
    // fallback.
    return shareLink(payload, nav);
}
